import React, { useEffect, useState } from "react";
import * as XLSX from "xlsx";
import { listClasses, listSubjects, listStudents, getStudentCounts } from "./happyschoolApi";

// Webview nhúng trong Zalo/Messenger/Instagram/Line/WeChat CHỈ chặn tải file
// (Blob download) trên iOS (WKWebView giới hạn) — Android webview vẫn tải
// bình thường qua Download Manager của hệ thống nên không cần chặn.
function isBlockedInAppBrowser() {
  const ua = navigator.userAgent || "";
  const isIOS = /iPhone|iPad|iPod/i.test(ua);
  const isInApp = /Zalo|FBAN|FBAV|Instagram|Line\/|MicroMessenger/i.test(ua);
  return isIOS && isInApp;
}
function downloadWorkbook(wb, fileName) {
  if (isBlockedInAppBrowser()) {
    window.alert(
      "Trình duyệt trong ứng dụng (Zalo/Messenger/...) trên iPhone không cho phép tải file.\n\n" +
      "Hãy bấm nút menu (⋮ hoặc ...) ở góc trên, chọn \"Mở bằng trình duyệt\" (Safari), rồi bấm Export lại."
    );
    return;
  }
  const wbArray = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  const blob = new Blob([wbArray], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// Quản lý học sinh (cổng giáo viên) — chia 2 mục:
//   - Lớp chủ nhiệm : classes.homeroomTeacherId === staffId của giáo viên
//   - Lớp chuyên môn : classes có classSubjects[subjectId].assignedTeacherId === staffId
// Chỉ đọc — bấm vào 1 lớp để xem danh sách học sinh của lớp đó.
const HappySchoolMyClasses = ({ schoolId, staffId }) => {
  const [tab, setTab] = useState("homeroom");
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedClass, setSelectedClass] = useState(null);
  const [students, setStudents] = useState([]);
  const [loadingStudents, setLoadingStudents] = useState(false);

  useEffect(() => {
    if (!schoolId) return;
    setLoading(true); setError("");
    Promise.all([listClasses(schoolId), listSubjects(schoolId), getStudentCounts(schoolId)])
      .then(([c, s, cnt]) => { setClasses(c || []); setSubjects(s || []); setCounts(cnt || {}); })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [schoolId]);

  const subjectName = (id) => (subjects.find((s) => s.id === id) || {}).name || id;

  const homeroomClasses = classes.filter((c) => c.homeroomTeacherId === staffId);
  const teachingClasses = classes
    .map((c) => ({ ...c, mySubjects: (c.subjects || []).filter((s) => s.assignedTeacherId === staffId) }))
    .filter((c) => c.mySubjects.length > 0);

  const exportClassExcel = () => {
    if (!selectedClass || !students.length) return;
    const header = ["Họ và tên", "Giới tính", "Ngày sinh", "Mã định danh", "SĐT"];
    const rows = students.map((s) => [s.name || "", s.gender || "", s.dob || "", s.personalId || "", s.phone || ""]);
    const ws = XLSX.utils.aoa_to_sheet([header, ...rows]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "HSSV");
    downloadWorkbook(wb, `HSSV_${selectedClass.name}.xlsx`);
  };

  const openClass = (c) => {
    setSelectedClass(c);
    setLoadingStudents(true); setError("");
    listStudents(schoolId, c.id)
      .then((list) => setStudents((list || []).slice().sort((a, b) => a.name.localeCompare(b.name, "vi"))))
      .catch((e) => setError(e.message))
      .finally(() => setLoadingStudents(false));
  };

  if (!staffId) {
    return (
      <p style={{ color: "#6b7280" }}>
        Chưa xác định được hồ sơ giáo viên của bạn — vào <b>"Xác thực thông tin"</b> trước để xem danh sách lớp.
      </p>
    );
  }

  if (selectedClass) {
    const mySubjNames = tab === "teaching" ? (teachingClasses.find((c) => c.id === selectedClass.id) || {}).mySubjects : null;
    return (
      <div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <button type="button" onClick={() => setSelectedClass(null)} style={ghost}>← Quay lại danh sách lớp</button>
          {students.length > 0 && (
            <button type="button" onClick={exportClassExcel} style={ghost}>⬇ Export</button>
          )}
        </div>
        <h3 style={{ margin: "12px 0 2px" }}>{selectedClass.name} — {students.length} học sinh</h3>
        {mySubjNames && mySubjNames.length > 0 && (
          <p style={{ color: "#6b7280", fontSize: 13, margin: "0 0 10px" }}>
            Bạn dạy: {mySubjNames.map((s) => subjectName(s.subjectId)).join(", ")}
          </p>
        )}
        {error && <p style={{ color: "#dc2626" }}>{error}</p>}
        {loadingStudents ? (
          <p>Đang tải...</p>
        ) : students.length === 0 ? (
          <p style={{ color: "#9ca3af" }}>Lớp chưa có học sinh.</p>
        ) : (
          <div style={{ overflowX: "auto", border: "1px solid #e5e7eb", borderRadius: 8 }}>
            <table style={{ borderCollapse: "collapse", fontSize: 13, width: "100%" }}>
              <thead>
                <tr>
                  <th style={th}>#</th>
                  <th style={{ ...th, textAlign: "left" }}>Họ và tên</th>
                  <th style={th}>Giới tính</th>
                  <th style={th}>Ngày sinh</th>
                  <th style={th}>Mã định danh</th>
                  <th style={th}>SĐT</th>
                </tr>
              </thead>
              <tbody>
                {students.map((s, i) => (
                  <tr key={s.id}>
                    <td style={{ ...td, textAlign: "center", color: "#9ca3af" }}>{i + 1}</td>
                    <td style={{ ...td, fontWeight: 600 }}>{s.name}</td>
                    <td style={{ ...td, textAlign: "center" }}>{s.gender || ""}</td>
                    <td style={{ ...td, textAlign: "center" }}>{s.dob || ""}</td>
                    <td style={{ ...td, textAlign: "center" }}>{s.personalId || ""}</td>
                    <td style={{ ...td, textAlign: "center" }}>{s.phone || ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  }

  const list = tab === "homeroom" ? homeroomClasses : teachingClasses;

  return (
    <div>
      <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
        <button type="button" onClick={() => setTab("homeroom")} style={tab === "homeroom" ? tabA : tabS}>
          🏠 Lớp chủ nhiệm ({homeroomClasses.length})
        </button>
        <button type="button" onClick={() => setTab("teaching")} style={tab === "teaching" ? tabA : tabS}>
          📚 Lớp chuyên môn ({teachingClasses.length})
        </button>
      </div>

      {error && <p style={{ color: "#dc2626" }}>{error}</p>}
      {loading ? (
        <p>Đang tải...</p>
      ) : list.length === 0 ? (
        <p style={{ color: "#9ca3af" }}>
          {tab === "homeroom" ? "Bạn chưa được phân công chủ nhiệm lớp nào." : "Bạn chưa được phân công dạy lớp nào."}
        </p>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 12 }}>
          {list.map((c) => (
            <div key={c.id} onClick={() => openClass(c)} style={card}>
              <div style={{ fontWeight: 700, color: "#1f2937" }}>{c.name}</div>
              <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>{counts[c.id] || 0} học sinh</div>
              {tab === "teaching" && (
                <div style={{ fontSize: 12, color: "#0f766e", marginTop: 4 }}>
                  {c.mySubjects.map((s) => subjectName(s.subjectId)).join(", ")}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const card = { cursor: "pointer", padding: 14, border: "1px solid #e5e7eb", borderRadius: 10, background: "#fff", transition: "all .15s" };
const ghost = { padding: "8px 14px", background: "#f3f4f6", color: "#374151", border: "1px solid #d1d5db", borderRadius: 8, cursor: "pointer" };
const tabS = { padding: "8px 14px", border: "1px solid #d1d5db", borderRadius: 999, background: "#fff", color: "#374151", cursor: "pointer", fontWeight: 600, fontSize: 13 };
const tabA = { ...tabS, background: "#0ea5a3", color: "#fff", borderColor: "#0ea5a3" };
const th = { border: "1px solid #e5e7eb", padding: "7px 8px", background: "#f9fafb", whiteSpace: "nowrap" };
const td = { border: "1px solid #e5e7eb", padding: "5px 8px" };

export default HappySchoolMyClasses;
